<template>
    <div>
        <div class="h6">
            <div class="d-flex align-items-center">Lesson Plan</div>
        </div>

        <b-spinner v-if="fetchingLessonPlan" small label="Loading..." class="mr-1" />
        <template v-else>
            <div v-if="!lesson_plan">
                <b-alert
                    variant="warning"
                    class="my-2"
                    show
                >
                    No Lesson Plan has been created for this article.
                </b-alert>
                <div class="text-center">
                    <b-btn
                        variant="outline-primary"
                        @click="createLessonPlan"
                        :disabled="creatingLessonPlan || disabled"
                    >
                        <b-spinner v-if="creatingLessonPlan" small label="Creating..." class="mr-1" />
                        <font-awesome-icon v-else :icon="['fad', 'plus-square']" fixed-width />
                        Create Lesson Plan
                    </b-btn>
                </div>
            </div>
            <template v-else>
                <TipTap
                    v-model="lesson_plan.lesson_plan"
                    class="lesson-plan-tiptap"
                    :exclude_table_resize="true"
                    :article="article"
                    :publication="publication"
                    :menu_items="[
                    'bold',
                    'italic',
                    'underline',
                    'ordered_list',
                    'bullet_list',
                    'interactive_media_link',
                    'image',
                    'hard_break',
                    'highlight',
                    'highlight_orange',
                    'highlight_blue',
                    'highlight_green',
                    'highlight_teal',
                    'highlight_purple',
                    'highlight_pink',
                    'highlight_black',
                    'highlight_white',
                    'edit_html',
                    'paragraph_alignment',
                    'horizontal_rule'
                ]"
                    @input="setContent('lesson_plan', $event)"
                    @blur="saveLessonPlan"
                    @update="saveLessonPlan"
                    @update_tiptap_table="saveLessonPlan"
                    :readonly="disabled"
                />

                <div class="d-flex">
                    <button
                        class="btn btn-warning my-3 ml-2 text-white"
                        @click="show_lp_preview = !show_lp_preview"
                    >
                        Show Lesson Plan Preview
                    </button>
                    <button
                        class="btn btn-primary my-3 ml-2 text-white"
                        @click="show_lp_print_preview(swoLessonPlan)"
                    >
                        Print Preview
                    </button>
                    <b-btn
                        class="ml-auto my-3"
                        variant="danger"
                        @click="deleteLessonPlan"
                        :disabled="disabled"
                    >
                        Delete
                    </b-btn>
                </div>

                <div
                    style="position: relative; max-height: 500px; overflow-y: auto; border: 2px solid orange;"
                    v-if="show_lp_preview"
                >
                    <div
                        v-html="swoLessonPlan"
                        class="p-4 lp-preview-container"
                        style="position: sticky; top: 0px; z-index: 5; border: 1px solid lightgray;"
                    />
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import TipTap from '@/components/tiptap/TipTap'

export default {
    components: {
        TipTap,
    },
    props: [
        'article',
        'publication',
        'disabled',
    ],
    data: () => ({
        lesson_plan: null,
        show_lp_preview: false,
        fetchingLessonPlan: false,
        creatingLessonPlan: false,
        savingLessonPlan: false,
    }),
    computed: {
        swoLessonPlan() {
            let return_string = ""
            const regex = /(sbp-img|merlin-img)/g

            if (this.lesson_plan.lesson_plan) {
                const review_lesson_plan = this.lesson_plan.lesson_plan
                return_string = review_lesson_plan.replace(/<p><\/p>/g, '<p></p>')
                return_string = return_string.replace(regex, 'img')

                const regex2 = /<interactive-media-link\b([^>]*)>\s*<\/interactive-media-link>/gi
                return_string = return_string.replace(regex2, (match, attributes) => {
                    const titleMatch = attributes.match(/\btitle="([^"]*)"/i)
                    const title = titleMatch ? titleMatch[1] : 'Open'

                    return `<a href="">${title}</a>`
                })
            }

            return return_string
        },
    },
    methods: {
        setContent(field, value) {
            if (value) {
                this.$set(this.lesson_plan, field, value)
            }
        },
        async fetchLessonPlan() {
            try {
                this.fetchingLessonPlan = true
                const response = await this.axios.get(
                    `/api/lesson_plan`,
                    {
                        params: {
                            'publication_id': this.publication.id,
                            'article_id': this.article.id,
                        }
                    }
                )
                if (response.data) {
                    this.lesson_plan = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not fetch Lesson Plan: ${e.message}`,
                    },
                })
            } finally {
                this.fetchingLessonPlan = false
            }
        },
        async createLessonPlan() {
            if (this.disabled || this.creatingLessonPlan) {
                return
            }

            try {
                this.creatingLessonPlan = true

                const response = await this.axios.post(
                    `/api/lesson_plan`,
                    {
                        publication_id: this.publication.id,
                        article_id: this.article.id,
                    }
                )
                if (response.data) {
                    this.lesson_plan = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not create Lesson Plan: ${e.message}`,
                    },
                })
            } finally {
                this.creatingLessonPlan = false
            }
        },
        async deleteLessonPlan() {
            const result = await this.$swal({
                html: 'Are you sure you want to delete this Lesson Plan?',
                showCancelButton: true,
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E74C3C',
                reverseButtons: true,
            })
            if (!result.value) {
                return
            }

            try {
                await this.axios.delete(`/api/lesson_plan/${this.lesson_plan.id}`)
                this.lesson_plan = null
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not delete Lesson Plan: ${e.message}`,
                    },
                })
            }
        },
        async saveLessonPlan() {
            if (this.disabled || this.savingLessonPlan) {
                return
            }

            try {
                this.savingLessonPlan = true
                const lesson_plan = this.lesson_plan.lesson_plan ? this.lesson_plan.lesson_plan : null

                await this.axios.patch(
                    `/api/lesson_plan/${this.lesson_plan.id}`,
                    {
                        lesson_plan,
                    }
                )

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not save lesson plan: ${e.message}`,
                    },
                })
            } finally {
                this.savingLessonPlan = false
            }
        },
        show_lp_print_preview(swoLessonPlan) {
            // Open a new window
            const printWindow = window.open('', '_blank')
            // Collect all <style> content from the current document
            let styles = ''
            document.querySelectorAll('style').forEach(style => {
                styles += style.innerHTML
            })
            styles += this.getLPStringCss()

            // Write the HTML and styles into the new window
            printWindow.document.open()
            printWindow.document.write(`
                <html>
                <head>
                    <title>Lesson Plan</title>
                    <style>
                        ${styles}
                    </style>
                </head>
                <body>
                    <div class='lp-preview-container'>${swoLessonPlan}</div>
                </body>
                </html>
            `)
            printWindow.document.close()

            // Wait for content to load and then trigger the print dialog
            setTimeout(() => {
                printWindow.print()
                printWindow.close()
            }, 1000)
        },
        getLPStringCss() {
            let stringCss = `
                .lp-preview-container {
                    line-height: 20px;
                    font-family: poppins !important;
                }
                .lp-preview-container ol {
                    line-height: 20px !important;
                    margin: 0px !important;
                }
                .lp-preview-container ol {
                    list-style-type: decimal !important;
                }
                .lp-preview-container ol ol {
                    list-style-type: upper-alpha !important;
                }
                .lp-preview-container ol ol ol {
                    list-style-type: upper-roman !important;
                }
                .lp-preview-container ol ol ol ol {
                    list-style-type: lower-alpha !important;
                }
                .lp-preview-container ol p {
                    margin-bottom: 0px !important;
                }
                .lp-preview-container p {
                    position:relative;
                    z-index: 2;
                }
                p{
                    position: relative;
                    z-index: 2;
                    min-height: 20px;
                }
                .lp-preview-container td{
                    position: relative;
                    z-index: 2;
                    border: 1px solid black !important;
                    vertical-align: top;
                    padding: 1em;
                }
                .lp-preview-container ul li{
                    height: 25px;
                }
                .lp-preview-container img[align="left"]{
                    padding-right: 5px;
                }
                .lp-preview-container img[align="right"]{
                    padding-left: 5px;
                }
                .lp-preview-container img[align="behind"]{
                    position: absolute;
                    z-index: 1;
                }
                .lp-preview-container img[align="front"]{
                    position: absolute;
                    z-index: 3;
                }
                .lp-preview-container img[align="topandbottom"]{
                    position: relative;
                    z-index: 3;
                }
                .lp-preview-container img[align="center"]{
                    position: relative;
                    z-index: 3;
                }
                .lp-preview-container img[align="center"][width="10%"], img[align="behind"][width="10%"], .lp-preview-container img[align="front"][width="10%"], .lp-preview-container img[align="topandbottom"][width="10%"]{
                left: 45%;
                }
                .lp-preview-container img[align="center"][width="15%"], img[align="behind"][width="15%"], .lp-preview-container img[align="front"][width="15%"], .lp-preview-container img[align="topandbottom"][width="15%"]{
                    left: 42.5%;
                }
                .lp-preview-container img[align="center"][width="20%"], img[align="behind"][width="20%"], .lp-preview-container img[align="front"][width="20%"], .lp-preview-container img[align="topandbottom"][width="20%"]{
                left: 40%;
                }
                .lp-preview-container img[align="center"][width="25%"], img[align="behind"][width="25%"], .lp-preview-container img[align="front"][width="25%"], .lp-preview-container img[align="topandbottom"][width="25%"]{
                left: 37.5%;
                }
                .lp-preview-container img[align="center"][width="30%"], img[align="behind"][width="30%"], .lp-preview-container img[align="front"][width="30%"], .lp-preview-container img[align="topandbottom"][width="30%"]{
                left: 35%;
                }
                .lp-preview-container img[align="center"][width="40%"], img[align="behind"][width="40%"], .lp-preview-container img[align="front"][width="40%"], .lp-preview-container img[align="topandbottom"][width="40%"]{
                left: 30%;
                }
                .lp-preview-container img[align="center"][width="45%"], img[align="behind"][width="45%"], .lp-preview-container img[align="front"][width="45%"], .lp-preview-container img[align="topandbottom"][width="45%"]{
                    left: 27.5%;
                }
                .lp-preview-container img[align="center"][width="50%"], img[align="behind"][width="50%"], .lp-preview-container img[align="front"][width="50%"], .lp-preview-container img[align="topandbottom"][width="50%"]{
                left: 25%;
                }
                .lp-preview-container img[align="center"][width="60%"], img[align="behind"][width="60%"], .lp-preview-container img[align="front"][width="60%"], .lp-preview-container img[align="topandbottom"][width="60%"]{
                left: 20%;
                }
                .lp-preview-container img[align="center"][width="70%"], img[align="behind"][width="70%"], .lp-preview-container img[align="front"][width="70%"], .lp-preview-container img[align="topandbottom"][width="70%"]{
                left: 15%;
                }
                .lp-preview-container img[align="center"][width="80%"], img[align="behind"][width="80%"], .lp-preview-container img[align="front"][width="80%"], .lp-preview-container img[align="topandbottom"][width="80%"]{
                left: 10%;
                }
                .lp-preview-container img[align="center"][width="90%"], img[align="behind"][width="90%"], .lp-preview-container img[align="front"][width="90%"], .lp-preview-container img[align="topandbottom"][width="90%"]{
                left: 5%;
                }
                .lp-preview-container img[align="center"][width="100%"], img[align="behind"][width="100%"], .lp-preview-container img[align="front"][width="100%"], .lp-preview-container img[align="topandbottom"][width="100%"]{
                left: 0%;
                }
                .lp-preview-container img[align="center"][width="33%"], img[align="behind"][width="33%"], .lp-preview-container img[align="front"][width="33%"], .lp-preview-container img[align="topandbottom"][width="33%"]{
                left: 33.3%;
                }
                .lp-preview-container img[align="center"][width="66%"], img[align="behind"][width="66%"], .lp-preview-container img[align="front"][width="66%"], .lp-preview-container img[align="topandbottom"][width="66%"]{
                left: 16.6%;
                }
                .merlin_image_center, .sbp_image_center{
                    position: relative;
                    z-index: 3;
                }
                img[width="5%"].merlin_image_center, img[width="5%"].sbp_image_center {
                left: 47.5%;
                }
                img[width="10%"].merlin_image_center, img[width="10%"].sbp_image_center {
                left: 45%;
                }
                img[width="25%"].merlin_image_center, img[width="25%"].sbp_image_center {
                left: 37.5%;
                }
                img[width="33%"].merlin_image_center, img[width="33%"].sbp_image_center {
                left: 33%;
                }
                img[width="50%"].merlin_image_center, img[width="50%"].sbp_image_center {
                left: 25%;
                }
                img[width="66%"].merlin_image_center, img[width="66%"].sbp_image_center {
                left: 16.5%;
                }
                img[width="75%"].merlin_image_center, img[width="75%"].sbp_image_center {
                left: 12.5%;
                }
                img[width="90%"].merlin_image_center, img[width="90%"].sbp_image_center {
                left: 5%;
                }
                img[width="95%"].merlin_image_center, img[width="95%"].sbp_image_center {
                left: 2.5%;
                }
                img[width="100%"].merlin_image_center, img[width="100%"].sbp_image_center {
                left: 0px;
                }
                .lp-preview-container table {
                    border-collapse: collapse;
                    width: 100%;
                }
                /* Preserve explicit alignment while defaulting unspecified table text to left */
                td[data-colwidth] p:not([style*="text-align"]) {
                    text-align: left !important;
                }
                @media print {
                    .lp-preview-container,
                    .lp-preview-container *,
                    .lp-preview-container table,
                    .lp-preview-container tr,
                    .lp-preview-container td,
                    .lp-preview-container th {
                        background: transparent !important;
                        background-color: transparent !important;
                    }
                    .lp-preview-container table {
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                    }
                    .lp-preview-container tr,
                    .lp-preview-container td,
                    .lp-preview-container th {
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                    }
                    .lp-preview-container tr:first-child {
                        page-break-after: avoid !important;
                        break-after: avoid-page !important;
                    }
                }
            `;

            for (let i = 1; i <= 1500; i++) {
                stringCss += `
                    td[data-colwidth="${i}"] {
                        width: ${i}px;
                    }
                `
            }

            return stringCss
        },
    },
    async mounted() {
        await this.fetchLessonPlan()
    },
}
</script>
